import { useState, useRef, useEffect } from "react";
import { m, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ReactGA from 'react-ga4';
// No import needed—public assets via direct path

// CSS keyframes for synced red/blue glow animation
const glowKeyframes = `
@keyframes logoGlow {
  0%, 100% {
    filter: drop-shadow(0 0 12px rgba(220,38,38,.75)) drop-shadow(0 0 48px rgba(220,38,38,.5)) drop-shadow(0 0 160px rgba(220,38,38,.4));
  }
  50% {
    filter: drop-shadow(0 0 12px rgba(37,99,235,.75)) drop-shadow(0 0 48px rgba(37,99,235,.5)) drop-shadow(0 0 160px rgba(37,99,235,.4));
  }
}
@keyframes redFade {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes blueFade {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}
@keyframes lineGlow {
  0%, 100% {
    box-shadow: 0 0 12px rgba(220,38,38,0.8), 0 0 24px rgba(220,38,38,0.4);
  }
  50% {
    box-shadow: 0 0 12px rgba(37,99,235,0.8), 0 0 24px rgba(37,99,235,0.4);
  }
}
`;

export default function PatrioticLogo() {
  const navigate = useNavigate();
  const [isScattering, setIsScattering] = useState(false);
  const logoRef = useRef(null);
  const [rippleStyle, setRippleStyle] = useState({});
  const companiesPreloadedRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    setIsMobile(mq.matches);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const preloadCompanies = () => {
    if (companiesPreloadedRef.current) return;
    companiesPreloadedRef.current = true;
    import("../pages/Companies");
  };

  // Mouse interactivity: Track cursor for subtle 3D tilt
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-0.5, 0.5], [-15, 15]); // Bolder tilt as before
  const rotateY = useTransform(x, [-0.5, 0.5], [-15, 15]);

  useEffect(() => {
    if (isScattering && logoRef.current) {
      const rect = logoRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const size = Math.max(rect.width, rect.height);
      setRippleStyle({
        left: `${x - size / 2}px`,
        top: `${y - size / 2}px`,
        width: `${size}px`,
        height: `${size}px`,
      });
    }
  }, [isScattering]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    x.set((e.clientX - rect.left - width / 2) / width);
    y.set((e.clientY - rect.top - height / 2) / height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const handleClick = (source = 'logo') => {
    if (process.env.REACT_APP_GA_ID) {
      ReactGA.event('home_cta_click', { cta_location: source });
    }
    if (isScattering) return;
    setIsScattering(true);
  };

  const handleAnimationComplete = () => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    navigate("/companies");
  };

  return (
    <>
    <style>{glowKeyframes}</style>
    <div
    className="logo-container relative w-full max-w-xs sm:max-w-md md:max-w-xl mx-auto"
    onMouseMove={isMobile ? undefined : handleMouseMove}
    onMouseLeave={isMobile ? undefined : handleMouseLeave}
    onMouseEnter={preloadCompanies}
    onFocus={preloadCompanies}
    onTouchStart={preloadCompanies}
  >
    {/* Stack logo + button tightly */}
    <div className="relative flex flex-col items-center gap-10"> {/* gap-2 <— closer; use gap-1 for tighter */}
      <m.img
        ref={logoRef}
        src="/logos/ohlogo.png"
        alt="Operations Holdings Emblem"
        fetchPriority="high"
        loading="eager"
        className="w-64 sm:w-80 md:w-96 h-auto mx-auto relative z-10 cursor-pointer"
        style={{
          ...(isMobile ? {} : { rotateX, rotateY, transformPerspective: 500 }),
          willChange: "transform, filter",
          animation: isScattering ? "none" : "logoGlow 6s ease-in-out infinite",
        }}
        initial={{ scale: 0.95, opacity: 0.9 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        onClick={() => handleClick('logo')}
        onMouseEnter={preloadCompanies}
        onFocus={preloadCompanies}
        onTouchStart={preloadCompanies}
        role="button"
        tabIndex={0}
        aria-label="Explore our proud portfolio"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      />

      <m.button
        onClick={() => handleClick('view_companies_button')}
        onMouseEnter={preloadCompanies}
        onFocus={preloadCompanies}
        onTouchStart={preloadCompanies}
        className="relative px-10 py-4 group overflow-hidden"
        whileHover="hover"
        initial="initial"
        whileTap={{ scale: 0.98 }}
        aria-label="Enter Companies"
      >
        {/* Top accent line with synced red/blue glow matching logo */}
        <m.div 
          className="absolute top-0 left-0 w-full h-[2px] max-sm:hidden"
          variants={{ initial: { scaleX: 0, opacity: 0 }, hover: { scaleX: 1, opacity: 1 } }}
          transition={{ duration: 0.6, ease: "circOut" }}
        >
          {/* Red layer */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: "linear-gradient(to right, transparent, rgba(220,38,38,0.9), transparent)",
              animation: "redFade 6s ease-in-out infinite",
            }}
          />
          {/* Blue layer */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: "linear-gradient(to right, transparent, rgba(37,99,235,0.9), transparent)",
              animation: "blueFade 6s ease-in-out infinite",
            }}
          />
          {/* Glow effect */}
          <div 
            className="absolute inset-0 blur-sm"
            style={{ animation: "lineGlow 6s ease-in-out infinite" }}
          />
        </m.div>
        {/* Bottom accent line with synced red/blue glow matching logo */}
        <m.div 
          className="absolute bottom-0 left-0 w-full h-[2px] max-sm:hidden"
          variants={{ initial: { scaleX: 0, opacity: 0 }, hover: { scaleX: 1, opacity: 1 } }}
          transition={{ duration: 0.6, ease: "circOut" }}
        >
          {/* Red layer */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: "linear-gradient(to right, transparent, rgba(220,38,38,0.9), transparent)",
              animation: "redFade 6s ease-in-out infinite",
            }}
          />
          {/* Blue layer */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: "linear-gradient(to right, transparent, rgba(37,99,235,0.9), transparent)",
              animation: "blueFade 6s ease-in-out infinite",
            }}
          />
          {/* Glow effect */}
          <div 
            className="absolute inset-0 blur-sm"
            style={{ animation: "lineGlow 6s ease-in-out infinite" }}
          />
        </m.div>

        {/* Text Content */}
        <span className="relative z-10 font-['Cinzel'] font-bold tracking-[0.2em] text-black uppercase text-xs sm:text-sm flex items-center gap-3">
          View Companies
          <m.svg 
            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            aria-hidden="true"
            variants={{ initial: { x: 0, opacity: 0.6 }, hover: { x: 4, opacity: 1 } }}
          >
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </m.svg>
        </span>
      </m.button>
    </div>

    {/* Ripple overlay stays the same, still expands from logo center */}
    <AnimatePresence>
      {isScattering && (
        <m.div
          className="fixed z-40 bg-gradient-to-r from-red-500 via-white to-blue-600 opacity-20 rounded-full"
          style={{ ...rippleStyle, transformOrigin: "center center" }}
          initial={{ scale: 1, opacity: 0.3 }}
          animate={{ scale: [1, 4, 6], opacity: [0.3, 0.2, 0], rotate: [0, 5, -5] }}
          transition={{ duration: 1.2, ease: "easeInOut", times: [0, 0.4, 1] }}
          onAnimationComplete={handleAnimationComplete}
        />
      )}
    </AnimatePresence>
  </div>
  </>
  );
}